import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';

import { Button, Field, Modal, Select, Text } from '@hotjar/hotsauce';
import type { IdentityProviderModel } from '@hotjar/webapp/domains/sso/model';
import type { Organization } from '@hotjar/organization';

interface Props {
  isOpen: boolean;
  onSubmit: (organization: Organization) => void;
  onCancel: () => void;
  identityProvider: IdentityProviderModel;
  organizations: Organization[];
}

interface Form {
  organizationId: string;
}

const S = {
  StyledField: styled(Field)`
    width: 100%;
  `,
};

export const SelectOrganizationModal = ({
  isOpen,
  onSubmit,
  onCancel,
  identityProvider,
  organizations,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { register, handleSubmit, errors } = useForm<Form>({
    mode: 'onChange',
  });

  const findOrganizationById = useCallback(
    (organizationId: string) =>
      organizations.find((organization) => organization.id === parseInt(organizationId, 10)),
    [organizations],
  );

  const isOrganizationAlreadyConnected = useCallback(
    (organizationId: string) => {
      return identityProvider.organizations.some(
        (idpOrganization) => idpOrganization.id === parseInt(organizationId, 10),
      );
    },
    [identityProvider.organizations],
  );

  const title = t('common:settings.sso.connect-organization-modal.title', {
    displayName: identityProvider.displayName,
  });

  return (
    <Modal
      isOpen={isOpen}
      contentLabel={title}
      onClose={onCancel}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      size="m"
    >
      <form
        onSubmit={handleSubmit(({ organizationId }) => {
          const organization = findOrganizationById(organizationId);

          if (organization) {
            onSubmit(organization);
          }
        })}
        noValidate
      >
        <Modal.Header title={title} />

        <Modal.Body>
          <Text as="p">
            {t('common:settings.sso.connect-organization-modal.description', {
              loginId: identityProvider.loginId,
            })}
          </Text>

          <S.StyledField
            labelText={t('common:settings.sso.connect-organization-modal.select-label')}
            inputId="organizationId"
            isInvalid={!!errors.organizationId}
            invalidTextId="organizationIdErrorMessage"
            invalidText={errors.organizationId?.message}
            isRequired
          >
            <Select
              id="organizationId"
              name="organizationId"
              ref={register({
                required: t('common:settings.sso.connect-organization-modal.required') as string,
                validate: (organizationId: string) =>
                  !isOrganizationAlreadyConnected(organizationId) ||
                  (t('common:settings.sso.connect-organization-modal.already-connected') as string),
              })}
            >
              <option value="">
                {t('common:settings.sso.connect-organization-modal.select-placeholder')}
              </option>
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </Select>
          </S.StyledField>
        </Modal.Body>

        <Modal.Footer>
          <Button kind="ghost" onClick={onCancel}>
            {t('common:settings.sso.connect-organization-modal.cancel')}
          </Button>

          <Button kind="primary" type="submit" data-testid="connect-organization-modal.submit">
            {t('common:settings.sso.connect-organization-modal.submit')}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
